// Public AI chat — streamed ask endpoint.
//
// POST {baseURL}/public/ai/ask  → streamed plain-text body.
// Same public API base as /api/v1/public/* (publicBooking.ts). No auth header
// for now (server-side rate-limited); `token` is wired through for later.
//
// We use fetch (not axios) so we can read the response body chunk-by-chunk via
// a ReadableStream reader + TextDecoder and append tokens as they arrive.

import { baseURL } from "../utils/constants";

export interface AiTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AskParams {
  hospitalId: string;
  message: string;
  history: AiTurn[];
  signal?: AbortSignal;
  /** Reserved for future auth — adds `Authorization: Bearer <token>` when set. */
  token?: string | null;
  /** Called with each streamed text chunk as it arrives. */
  onChunk: (chunk: string) => void;
}

/** Carries the HTTP status so callers can special-case 429 (rate limited). */
export class AiAskError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AiAskError";
    this.status = status;
  }
}

export async function streamPublicAiAsk(params: AskParams): Promise<string> {
  const { hospitalId, message, history, signal, token, onChunk } = params;

  const res = await fetch(`${baseURL}/public/ai/ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ hospitalId, message, history }),
    signal,
  });

  if (!res.ok) {
    // Non-200 → body is JSON { error }. Surface it (429 handled by caller).
    let msg = "";
    try {
      const data = await res.json();
      msg = data?.error || data?.message || "";
    } catch {
      /* body wasn't JSON — leave msg empty */
    }
    throw new AiAskError(res.status, msg);
  }

  // No streaming body support → fall back to a single text read.
  if (!res.body || typeof res.body.getReader !== "function") {
    const text = await res.text();
    if (text) onChunk(text);
    return text;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    if (chunk) {
      full += chunk;
      onChunk(chunk);
    }
  }
  const tail = decoder.decode();
  if (tail) {
    full += tail;
    onChunk(tail);
  }
  return full;
}
